import React, { useState, useEffect } from 'react';
import * as Images from '../../../assets';
import { ButtonOrange } from '../../Buttons';
import UpdatedAnimatedComponent from '../../AnimationWrapperComponent';
import { fetchWithFormData } from '../../../apiGeneralFunction';
import { trackCustomEvent } from '../../../event_firebase';

const OurITProjects = ({ titleRef, isVisible }) => {
    const [products, setProducts] = useState([]);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    useEffect(() => {
        const formData = new FormData();
        fetchWithFormData('headway_it', formData)
            .then(data => {
                if (data?.status === true) {
                    setProducts(data?.data?.our_products || []);
                    trackCustomEvent('headway_it', { status: true });
                } else {
                    setProducts([]);
                    trackCustomEvent('headway_it', { status: false });
                }
            })
            .catch(() => setProducts([]));
    }, []);

    // Cleanup on component unmount
    useEffect(() => {
        return () => {
            document.body.style.position = '';
            document.body.style.top = '';
            document.body.style.left = '';
            document.body.style.right = '';
            document.body.removeAttribute('data-scroll-y');
        };
    }, []);

    const openModal = (product) => {
        setSelectedProduct(product);
        setIsModalOpen(true);

        // Store current scroll position and prevent body scroll
        const scrollY = window.scrollY;
        document.body.style.position = 'fixed';
        document.body.style.top = `-${scrollY}px`;
        document.body.style.left = '0';
        document.body.style.right = '0';
        document.body.setAttribute('data-scroll-y', scrollY.toString());
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedProduct(null);

        // Restore scroll position
        const scrollY = parseInt(document.body.getAttribute('data-scroll-y') || '0');
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        document.body.removeAttribute('data-scroll-y');
        window.scrollTo(0, scrollY);
    };

    return (
        <>
            <div className="flex justify-center items-center px-16 py-12 bg-white max-md:px-5 max-sm:py-0" >
                <div className="max-w-full w-[1296px]" ref={titleRef}>
                    <div
                        className={`text-start text-5xl font-medium max-md:max-w-full max-md:text-4xl ${isVisible ? 'about-us_title_animation_fade_in_right' : ''}`}
                    >
                        <span className=" leading-[58px]">Our</span>{" "}
                        <span className="font-bold text-orange-500 leading-[58px]">
                            Projects
                        </span>
                    </div>
                </div>
            </div>
            <section className="bg-white p-8">
                <div className="mx-auto grid max-w-7xl grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                    {products.map((product) => (
                        <div
                            key={product.id}
                            className="group relative h-[300px] w-full overflow-hidden rounded-lg shadow-[0_4px_20px_0_rgba(255,115,0,0.2)]"
                              onClick={() => openModal(product)} 
                        >
                            {/* Image fills full card by default, shrinks on hover */}
                            <div
                                className="absolute inset-0 z-0 transition-all duration-500 bg-cover bg-center group-hover:w-1/2 group-hover:h-1/2 group-hover:top-1/2 group-hover:left-0"
                                style={{
                                    backgroundImage: `url(${product.photo})`,
                                }}
                            ></div>

                            {/* Title + Tagline – hidden by default, appear in top half on hover */}
                            <div className="top-0 left-0 z-10 h-1/2 w-full bg-orange-600/90 p-4 opacity-0 transition-opacity duration-500 group-hover:opacity-100 flex flex-col justify-center">
                                <h3 className="mb-2 text-xl font-semibold text-white">{product.title}</h3>
                                <p className="text-sm font-light text-white">{product.tagline}</p>
                            </div>

                            {/* Read More Button – bottom right, visible on hover */}
                            <div className="bottom-0 mt-12 mr-3 md:mr-10 lg:mr-4 xl:mr-7 2xl:mr-8 3xl:mr-6 h-2/2 w-2/2 z-10 flex items-end justify-end p-4 opacity-0 pointer-events-none transition-opacity duration-500 group-hover:opacity-100 group-hover:pointer-events-auto">
                                <button
                                    className="text-black font-medium hover:text-orange-500 transition"
                                    onClick={() => openModal(product)}
                                >
                                    Read More <i className="fa fa-external-link"></i>
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </section>
            {isModalOpen && selectedProduct && (
                <div
                    className="fixed inset-0 z-50 bg-black bg-opacity-70"
                    style={{
                        height: '100vh',
                        width: '100vw',
                        top: 0,
                        left: 0
                    }}
                    onClick={closeModal}
                >
                    <div className="flex items-center justify-center min-h-full p-4 mt-5">
                        <div
                            className="relative bg-white rounded-xl w-full max-w-lg shadow-lg"
                            onClick={(e) => e.stopPropagation()}
                            id="productModal"
                        >
                            {/* Close Icon */}
                            <button
                                className="absolute top-4 right-4 text-gray-500 hover:text-orange-500 text-2xl z-10"
                                onClick={closeModal}
                                aria-label="Close"
                            >
                                &times;
                            </button>
                            {/* Banner Image */}
                            <img
                                src={selectedProduct.product_banner}
                                alt={selectedProduct.title}
                                className="w-full h-48 object-cover rounded-t-xl"
                            />
                            {/* Content */}
                            <div className="p-6">
                                <h3 className="text-2xl font-bold text-zinc-800 mb-2">{selectedProduct.title}</h3>
                                <div
                                    className="text-base text-zinc-700 mb-4 max-h-60 overflow-y-auto"
                                    dangerouslySetInnerHTML={{ __html: selectedProduct.desc }}
                                />
                                <div className="flex gap-4 mt-4">
                                    {selectedProduct.play_store && (
                                        <a
                                            href={selectedProduct.play_store}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="focus:outline-none"
                                            aria-label="Play Store"
                                        >
                                            <img src={Images.google_play} alt="Play Store" className="w-30 h-15" />
                                        </a>
                                    )}
                                    {selectedProduct.app_store && (
                                        <a
                                            href={selectedProduct.app_store}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="focus:outline-none"
                                            aria-label="App Store"
                                        >
                                            <img src={Images.app_store} alt="App Store" className="w-30 h-15" />
                                        </a>
                                    )}
                                    {selectedProduct.web_url && (
                                        <a
                                            href={selectedProduct.web_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="focus:outline-none"
                                            aria-label="Website"
                                        >
                                            <img src={Images.website_link} alt="Website" className="w-30 h-15" />
                                        </a>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}

export default UpdatedAnimatedComponent(OurITProjects)